/* eslint-disable react/jsx-closing-bracket-location, react/jsx-sort-props */
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useTeamColors } from 'hooks';

import SEO from 'components/SEO/SEO';
import { BackgroundImg } from 'components/Header/style.js';
import TopNav from 'components/TopNav';
import { detectInAppWebView } from 'utils/browserDetection';
import backgroundImg from 'assets/img/basic-background.jpg';

import { StyledHeader } from 'components/App/style';
import { Wrapper, StyledBodyContainer, HighlightedText } from './style.js';

const Policy = () => {
  const siteName = useSelector((state) => state.configuration?.data?.site_name);
  const location = useLocation();
  const isSupportPage = location.pathname.includes('support');
  const { mainColor } = useTeamColors();
  const isPrivacyPage = location.pathname.includes('privacy');
  const [activeTab, setActiveTab] = useState(
    isSupportPage ? 'contact' : 'policy'
  );

  // Check if we should show header (same logic as MainPage)
  const { isInAppWebView } = detectInAppWebView();
  const shouldShowHeader = !isInAppWebView;

  // Set page-specific SEO data based on route
  const pageTitle = isPrivacyPage
    ? 'Privacy Policy'
    : isSupportPage
      ? 'Support & Contact'
      : 'Privacy Policy & Terms';

  const pageDescription = isPrivacyPage
    ? 'Privacy policy - how we handle your data and protect your privacy.'
    : isSupportPage
      ? 'Contact our support team for help with your account or questions.'
      : 'Privacy policy and terms of service for our sports countdown tracker.';

  const canonicalPath = isPrivacyPage
    ? '/privacy'
    : isSupportPage
      ? '/support'
      : '/policy';

  return (
    <>
      {shouldShowHeader === true ? (
        <StyledHeader>
          <TopNav />
        </StyledHeader>
      ) : null}
      <StyledBodyContainer
        style={{
          height: shouldShowHeader ? 'calc(100vh - 80px)' : '100vh',
          overflow: 'hidden',
          background: shouldShowHeader
            ? 'rgba(0, 0, 0, 0.9)'
            : 'rgba(0, 0, 0, 0.9)',
        }}
      >
        <SEO
          canonical={`${window.location.origin}${canonicalPath}`}
          description={pageDescription}
          title={pageTitle}
        />
        <BackgroundImg $imgUrl={`${backgroundImg}`} />

        {/* Clean Header */}
        <div
          style={{
            padding: '48px 20px 16px 20px',
            maxWidth: '800px',
            margin: '0 auto',
            position: 'relative',
          }}
        >
          {/* Centered Title Area */}
          <div
            style={{
              textAlign: 'center',
              paddingTop: '24px',
            }}
          >
            <p
              style={{
                fontSize: '1.1rem',
                lineHeight: '1.6',
                marginTop: '0',
                textAlign: 'center',
                color: '#ccc',
              }}
            >
              Welcome to{' '}
              {siteName ? (
                <>
                  <HighlightedText $color={mainColor}>
                    {siteName}
                  </HighlightedText>
                  !
                </>
              ) : (
                <HighlightedText $color={mainColor}>
                  our service
                </HighlightedText>
              )}
              <br />
              We keep things simple when it comes to your privacy and how you
              use our service.
            </p>
          </div>
        </div>

        <Wrapper
          $headingColor={mainColor}
          $linkColor={mainColor}
          style={{ height: 'calc(100vh - 120px)', overflow: 'hidden' }}
        >
          {/* Fixed width container */}
          <div
            style={{
              maxWidth: '800px',
              margin: '0 auto',
              padding: '0 20px',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Tab Navigation */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                borderBottom: '2px solid #333',
                marginBottom: '40px',
                gap: '2px',
              }}
            >
              <button
                type="button"
                onClick={() => setActiveTab('policy')}
                style={{
                  padding: '14px 32px',
                  border: 'none',
                  background:
                    activeTab === 'policy' ? mainColor : 'transparent',
                  color: activeTab === 'policy' ? 'white' : '#999',
                  cursor: 'pointer',
                  borderRadius: '12px 12px 0 0',
                  fontWeight: activeTab === 'policy' ? '600' : '500',
                  fontSize: '16px',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  minWidth: '140px',
                }}
              >
                Privacy & Terms
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('contact')}
                style={{
                  padding: '14px 32px',
                  border: 'none',
                  background:
                    activeTab === 'contact' ? mainColor : 'transparent',
                  color: activeTab === 'contact' ? 'white' : '#999',
                  cursor: 'pointer',
                  borderRadius: '12px 12px 0 0',
                  fontWeight: activeTab === 'contact' ? '600' : '500',
                  fontSize: '16px',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  minWidth: '140px',
                }}
              >
                Contact Us
              </button>
            </div>

            {/* Tab Content with custom scrollbar - fills remaining height */}
            <div
              style={{
                flex: 1,
                overflowY: 'auto',
                paddingRight: '10px',
                minHeight: 0, // Important for flex child to scroll
                minWidth: '760px', // Ensure consistent width regardless of content
                // Custom scrollbar styles
                scrollbarWidth: 'thin',
                scrollbarColor: '${mainColor} #2a2a2a',
              }}
            >
              {activeTab === 'policy' ? (
                <div style={{ paddingBottom: '20px' }}>
                  <h2
                    style={{
                      fontSize: '1.5rem',
                      fontWeight: '600',
                      marginTop: '40px',
                      marginBottom: '16px',
                      color: '#fff',
                      borderBottom: '2px solid ${mainColor}',
                      paddingBottom: '8px',
                    }}
                  >
                    What We Do
                  </h2>
                  <p
                    style={{
                      lineHeight: '1.7',
                      marginBottom: '24px',
                      color: '#ccc',
                    }}
                  >
                    We provide events countdown and event tracking services. You
                    can create accounts, track events, and get notifications
                    about your favorite events.
                  </p>

                  <h2
                    style={{
                      fontSize: '1.5rem',
                      fontWeight: '600',
                      marginTop: '40px',
                      marginBottom: '16px',
                      color: '#fff',
                      borderBottom: '2px solid ${mainColor}',
                      paddingBottom: '8px',
                    }}
                  >
                    Information We Collect
                  </h2>
                  <p
                    style={{
                      lineHeight: '1.7',
                      marginBottom: '16px',
                      color: '#ccc',
                    }}
                  >
                    We only collect what we need to make the service work:
                  </p>
                  <ul
                    style={{
                      lineHeight: '1.7',
                      marginBottom: '24px',
                      paddingLeft: '20px',
                      color: '#ccc',
                    }}
                  >
                    <li style={{ marginBottom: '8px' }}>
                      <strong style={{ color: mainColor }}>
                        Your email address
                      </strong>{' '}
                      - for your account and notifications
                    </li>
                    <li style={{ marginBottom: '8px' }}>
                      <strong style={{ color: mainColor }}>
                        Location data
                      </strong>{' '}
                      - to calculate local event times and show relevant content
                    </li>
                    <li style={{ marginBottom: '8px' }}>
                      <strong style={{ color: mainColor }}>
                        Basic usage info
                      </strong>{' '}
                      - like which pages you visit and when
                    </li>
                    <li style={{ marginBottom: '8px' }}>
                      <strong style={{ color: mainColor }}>
                        Social login info
                      </strong>{' '}
                      - if you sign in with Google or Facebook
                    </li>
                  </ul>

                  <h2
                    style={{
                      fontSize: '1.5rem',
                      fontWeight: '600',
                      marginTop: '40px',
                      marginBottom: '16px',
                      color: '#fff',
                      borderBottom: '2px solid ${mainColor}',
                      paddingBottom: '8px',
                    }}
                  >
                    Location Data
                  </h2>
                  <p
                    style={{
                      lineHeight: '1.7',
                      marginBottom: '16px',
                      color: '#ccc',
                    }}
                  >
                    We access and use location data to:
                  </p>
                  <ul
                    style={{
                      lineHeight: '1.7',
                      marginBottom: '24px',
                      paddingLeft: '20px',
                      color: '#ccc',
                    }}
                  >
                    <li style={{ marginBottom: '8px' }}>
                      <strong style={{ color: mainColor }}>
                        Calculate local event times
                      </strong>{' '}
                      - show countdowns in your timezone
                    </li>
                    <li style={{ marginBottom: '8px' }}>
                      <strong style={{ color: mainColor }}>
                        Display relevant content
                      </strong>{' '}
                      - show events and information appropriate to your region
                    </li>
                    <li style={{ marginBottom: '8px' }}>
                      <strong style={{ color: mainColor }}>
                        Improve accuracy
                      </strong>{' '}
                      - ensure event times calculations are correct
                    </li>
                  </ul>
                  <p
                    style={{
                      lineHeight: '1.7',
                      marginBottom: '24px',
                      color: '#ccc',
                    }}
                  >
                    Location access is optional but recommended for the best
                    experience. We only collect general location information
                    (city/region) and do not store precise GPS coordinates. You
                    can disable location access in your device settings at any
                    time.
                  </p>

                  <h2
                    style={{
                      fontSize: '1.5rem',
                      fontWeight: '600',
                      marginTop: '40px',
                      marginBottom: '16px',
                      color: '#fff',
                      borderBottom: '2px solid ${mainColor}',
                      paddingBottom: '8px',
                    }}
                  >
                    How We Use Your Information
                  </h2>
                  <p
                    style={{
                      lineHeight: '1.7',
                      marginBottom: '16px',
                      color: '#ccc',
                    }}
                  >
                    We use your information to:
                  </p>
                  <ul
                    style={{
                      lineHeight: '1.7',
                      marginBottom: '24px',
                      paddingLeft: '20px',
                      color: '#ccc',
                    }}
                  >
                    <li style={{ marginBottom: '8px' }}>
                      Keep your account working
                    </li>
                    <li style={{ marginBottom: '8px' }}>
                      Calculate accurate local event times
                    </li>
                    <li style={{ marginBottom: '8px' }}>
                      Send you game updates and notifications
                    </li>
                    <li style={{ marginBottom: '8px' }}>Improve our service</li>
                    <li style={{ marginBottom: '8px' }}>
                      Contact you if needed
                    </li>
                  </ul>

                  <h2
                    style={{
                      fontSize: '1.5rem',
                      fontWeight: '600',
                      marginTop: '40px',
                      marginBottom: '16px',
                      color: '#fff',
                      borderBottom: '2px solid ${mainColor}',
                      paddingBottom: '8px',
                    }}
                  >
                    Cookies & Tracking
                  </h2>
                  <p
                    style={{
                      lineHeight: '1.7',
                      marginBottom: '24px',
                      color: '#ccc',
                    }}
                  >
                    We use cookies to remember your preferences and keep you
                    logged in. You can disable cookies in your browser, but some
                    features might not work properly.
                  </p>

                  <h2
                    style={{
                      fontSize: '1.5rem',
                      fontWeight: '600',
                      marginTop: '40px',
                      marginBottom: '16px',
                      color: '#fff',
                      borderBottom: '2px solid ${mainColor}',
                      paddingBottom: '8px',
                    }}
                  >
                    Your Rights
                  </h2>
                  <p
                    style={{
                      lineHeight: '1.7',
                      marginBottom: '16px',
                      color: '#ccc',
                    }}
                  >
                    You can:
                  </p>
                  <ul
                    style={{
                      lineHeight: '1.7',
                      marginBottom: '24px',
                      paddingLeft: '20px',
                      color: '#ccc',
                    }}
                  >
                    <li style={{ marginBottom: '8px' }}>
                      Update or delete your account anytime
                    </li>
                    <li style={{ marginBottom: '8px' }}>
                      Control location access through your device settings
                    </li>
                    <li style={{ marginBottom: '8px' }}>
                      Ask us what information we have about you
                    </li>
                    <li style={{ marginBottom: '8px' }}>
                      Request we delete your information
                    </li>
                    <li style={{ marginBottom: '8px' }}>
                      Opt out of notifications
                    </li>
                  </ul>

                  <h2
                    style={{
                      fontSize: '1.5rem',
                      fontWeight: '600',
                      marginTop: '40px',
                      marginBottom: '16px',
                      color: '#fff',
                      borderBottom: '2px solid ${mainColor}',
                      paddingBottom: '8px',
                    }}
                  >
                    Data Security
                  </h2>
                  <p
                    style={{
                      lineHeight: '1.7',
                      marginBottom: '24px',
                      color: '#ccc',
                    }}
                  >
                    We protect your information with standard security measures.
                    While no system is 100% secure, we do our best to keep your
                    data safe.
                  </p>

                  <h2
                    style={{
                      fontSize: '1.5rem',
                      fontWeight: '600',
                      marginTop: '40px',
                      marginBottom: '16px',
                      color: '#fff',
                      borderBottom: '2px solid ${mainColor}',
                      paddingBottom: '8px',
                    }}
                  >
                    Third-Party Services
                  </h2>
                  <p
                    style={{
                      lineHeight: '1.7',
                      marginBottom: '24px',
                      color: '#ccc',
                    }}
                  >
                    We use Google and Facebook for login, and other services to
                    make our app work. These services have their own privacy
                    policies.
                  </p>

                  <h2
                    style={{
                      fontSize: '1.5rem',
                      fontWeight: '600',
                      marginTop: '40px',
                      marginBottom: '16px',
                      color: '#fff',
                      borderBottom: '2px solid ${mainColor}',
                      paddingBottom: '8px',
                    }}
                  >
                    Age Restrictions
                  </h2>
                  <p
                    style={{
                      lineHeight: '1.7',
                      marginBottom: '24px',
                      color: '#ccc',
                    }}
                  >
                    Our service is not intended for children under 13. If we
                    discover we've collected information from a child under 13,
                    we'll delete it promptly.
                  </p>

                  <h2
                    style={{
                      fontSize: '1.5rem',
                      fontWeight: '600',
                      marginTop: '40px',
                      marginBottom: '16px',
                      color: '#fff',
                      borderBottom: '2px solid ${mainColor}',
                      paddingBottom: '8px',
                    }}
                  >
                    Terms of Use
                  </h2>
                  <p
                    style={{
                      lineHeight: '1.7',
                      marginBottom: '16px',
                      color: '#ccc',
                    }}
                  >
                    By using our service, you agree to:
                  </p>
                  <ul
                    style={{
                      lineHeight: '1.7',
                      marginBottom: '24px',
                      paddingLeft: '20px',
                      color: '#ccc',
                    }}
                  >
                    <li style={{ marginBottom: '8px' }}>
                      Use the service responsibly and legally
                    </li>
                    <li style={{ marginBottom: '8px' }}>
                      Not spam or abuse the system
                    </li>
                    <li style={{ marginBottom: '8px' }}>Respect other users</li>
                    <li style={{ marginBottom: '8px' }}>
                      Keep your account information accurate
                    </li>
                  </ul>

                  <h2
                    style={{
                      fontSize: '1.5rem',
                      fontWeight: '600',
                      marginTop: '40px',
                      marginBottom: '16px',
                      color: '#fff',
                      borderBottom: '2px solid ${mainColor}',
                      paddingBottom: '8px',
                    }}
                  >
                    Service Availability
                  </h2>
                  <p
                    style={{
                      lineHeight: '1.7',
                      marginBottom: '24px',
                      color: '#ccc',
                    }}
                  >
                    We strive to keep the service running smoothly, but we can't
                    guarantee 100% uptime. We may need to update or maintain the
                    service occasionally.
                  </p>

                  <h2
                    style={{
                      fontSize: '1.5rem',
                      fontWeight: '600',
                      marginTop: '40px',
                      marginBottom: '16px',
                      color: '#fff',
                      borderBottom: '2px solid ${mainColor}',
                      paddingBottom: '8px',
                    }}
                  >
                    Changes to This Policy
                  </h2>
                  <p
                    style={{
                      lineHeight: '1.7',
                      marginBottom: '24px',
                      color: '#ccc',
                    }}
                  >
                    We may update this policy from time to time. We'll let you
                    know about significant changes by email or through the app.
                  </p>
                </div>
              ) : null}

              {activeTab === 'contact' ? (
                <div style={{ paddingBottom: '20px' }}>
                  <h2
                    style={{
                      fontSize: '1.5rem',
                      fontWeight: '600',
                      marginTop: '40px',
                      marginBottom: '16px',
                      color: '#fff',
                      borderBottom: '2px solid ${mainColor}',
                      paddingBottom: '8px',
                    }}
                  >
                    Get in Touch
                  </h2>
                  <p
                    style={{
                      lineHeight: '1.7',
                      marginBottom: '24px',
                      color: '#ccc',
                    }}
                  >
                    Questions about privacy or our service? We'd love to hear
                    from you!
                  </p>

                  <h2
                    style={{
                      fontSize: '1.5rem',
                      fontWeight: '600',
                      marginTop: '40px',
                      marginBottom: '16px',
                      color: '#fff',
                      borderBottom: '2px solid ${mainColor}',
                      paddingBottom: '8px',
                    }}
                  >
                    Email Us
                  </h2>
                  <p
                    style={{
                      lineHeight: '1.7',
                      marginBottom: '16px',
                      color: '#ccc',
                    }}
                  >
                    For general inquiries, support, or privacy questions:
                  </p>
                  <div
                    style={{
                      padding: '24px',
                      backgroundColor: '${mainColorRgba(0.1)}',
                      borderRadius: '12px',
                      border: '1px solid ${mainColorRgba(0.3)}',
                      textAlign: 'center',
                      marginBottom: '40px',
                    }}
                  >
                    <a
                      href="mailto:contact@districtapps.com"
                      style={{
                        display: 'inline-block',
                        padding: '14px 28px',
                        backgroundColor: mainColor,
                        color: 'white',
                        textDecoration: 'none',
                        borderRadius: '8px',
                        fontWeight: '600',
                        fontSize: '1.1rem',
                        transition: 'all 0.3s ease',
                        boxShadow: '0 4px 12px ${mainColorRgba(0.3)}',
                      }}
                    >
                      contact@districtapps.com
                    </a>
                  </div>

                  {process.env.REACT_APP_FIBERY_FORM_URL ? (
                    <div>
                      <h2
                        style={{
                          fontSize: '1.5rem',
                          fontWeight: '600',
                          marginTop: '40px',
                          marginBottom: '16px',
                          color: '#fff',
                          borderBottom: '2px solid ${mainColor}',
                          paddingBottom: '8px',
                        }}
                      >
                        Contact Form
                      </h2>
                      <div
                        style={{
                          padding: '20px',
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                          borderRadius: '12px',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                        }}
                      >
                        <iframe
                          src={process.env.REACT_APP_FIBERY_FORM_URL}
                          style={{
                            width: '100%',
                            height: '400px',
                            border: 'none',
                            borderRadius: '8px',
                          }}
                          title="contact-form"
                        />
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
            {/* Last Updated - Centered */}
            <div
              style={{
                textAlign: 'center',
                paddingTop: '20px',
                paddingBottom: '10px',
              }}
            >
              <p
                style={{
                  margin: '0',
                  color: '#999',
                  fontSize: '0.9rem',
                  fontWeight: '400',
                }}
              >
                Last updated:{' '}
                {new Date().toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </p>
            </div>
            {/* Back Button - Left Aligned */}
            {/* Removed - now using header for navigation */}
          </div>
        </Wrapper>
      </StyledBodyContainer>
    </>
  );
};

export default Policy;
